import { getLocale, t, tn } from '../../i18n';
import type { PickerOption } from '../../components/OptionPicker';
import type { ClientContractRow } from '../../services/clientService';

/** The site board's bucket for sites that predate client contracts. */
export const UNASSIGNED_CLIENT_KEY = 'unassigned';

export type SiteCountByClient = ReadonlyMap<string, number>;

const siteCountLabel = (count: number) => tn('{count} site', '{count} sites', count);

/** Contracts arrive newest first; a picker is scanned by name. */
const sortByName = (contracts: ClientContractRow[]) => {
  const collator = new Intl.Collator(getLocale(), { sensitivity: 'base', numeric: true });
  return [...contracts].sort((a, b) => collator.compare(a.name, b.name));
};

const toClientOption = (
  contract: ClientContractRow,
  siteCountByClient: SiteCountByClient,
): PickerOption => {
  const count = siteCountByClient.get(contract.id) ?? 0;
  return {
    value: contract.id,
    label: contract.name,
    description: contract.status === 'archived' ? t('Archived') : undefined,
    count,
    countLabel: siteCountLabel(count),
  };
};

/**
 * The clients the site board can be narrowed to, A-Z, then the unlinked
 * bucket. A client with no sites would filter the board to nothing, so it is
 * left out -- unless it is the active filter, which stays so the trigger can
 * name it and the user can see what they picked.
 */
export function buildSiteClientFilterOptions(
  contracts: ClientContractRow[],
  siteCountByClient: SiteCountByClient,
  activeFilter: string | null,
): PickerOption[] {
  const options = sortByName(contracts.filter((contract) => (
    (siteCountByClient.get(contract.id) ?? 0) > 0 || contract.id === activeFilter
  ))).map((contract) => toClientOption(contract, siteCountByClient));

  const unassignedCount = siteCountByClient.get(UNASSIGNED_CLIENT_KEY) ?? 0;
  if (unassignedCount > 0 || activeFilter === UNASSIGNED_CLIENT_KEY) {
    options.push({
      value: UNASSIGNED_CLIENT_KEY,
      label: t('No client linked'),
      count: unassignedCount,
      countLabel: siteCountLabel(unassignedCount),
      group: UNASSIGNED_CLIENT_KEY,
    });
  }

  return options;
}

/** Every client a new site can belong to, A-Z. Archived clients are the
 *  caller's to exclude: they cannot take new sites. */
export function buildNewSiteClientOptions(
  assignableClients: ClientContractRow[],
  siteCountByClient: SiteCountByClient,
): PickerOption[] {
  return sortByName(assignableClients)
    .map((contract) => toClientOption(contract, siteCountByClient));
}
